import axios, { AxiosInstance, AxiosError } from 'axios';
import { useAuthStore } from '@/store/auth';
import type {
  FileInfo,
  FileProbeResult,
  Block,
  UploadInitResponse,
  UploadCompleteResponse
} from '@/types';

const API_BASE = import.meta.env.VITE_API_URL || '/api';

class ApiClient {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE,
      timeout: 30000
    });

    this.client.interceptors.request.use((config) => {
      const token = useAuthStore.getState().token;
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });

    this.client.interceptors.response.use(
      (response) => response,
      (error: AxiosError) => {
        // Don't auto-logout on 401 - just reject the promise and let the UI handle it
        return Promise.reject(error);
      }
    );
  }

  async verifyToken(): Promise<boolean> {
    try {
      const response = await this.client.get('/auth/verify');
      return response.data.valid === true;
    } catch {
      return false;
    }
  }

  async login(password: string): Promise<{ success: boolean; token?: string }> {
    try {
      const response = await this.client.post('/auth/login', { password });
      return response.data;
    } catch (error: any) {
      return { success: false };
    }
  }

  async getBlocks(pageId: string): Promise<{ blocks: Block[] }> {
    const response = await this.client.get(`/pages/${pageId}/blocks`);
    return response.data;
  }

  async listFiles(recursive = false): Promise<FileInfo[]> {
    const response = await this.client.post('/files/list', { recursive });
    return response.data.files;
  }

  async probeFileSizes(urls: string[]): Promise<FileProbeResult[]> {
    const response = await this.client.post('/files/probe', { urls });
    return response.data.results;
  }

  async downloadFile(url: string, filename: string): Promise<Blob> {
    const response = await axios.get(`${API_BASE}/files/download`, {
      headers: {
        Authorization: `Bearer ${useAuthStore.getState().token}`,
        'X-File-Url': url,
        'X-Filename': filename
      },
      responseType: 'blob'
    });
    return response.data;
  }

  async initUpload(
    filename: string,
    contentType: string,
    size: number
  ): Promise<UploadInitResponse> {
    const response = await this.client.post('/files/upload/init', {
      filename,
      contentType,
      size
    });
    return response.data;
  }

  async uploadChunk(
    uploadId: string,
    chunkIndex: number,
    totalChunks: number,
    chunkData: Blob
  ): Promise<{ uploaded: boolean; nextChunk: number | null }> {
    const formData = new FormData();
    formData.append('chunk', chunkData);
    formData.append('uploadId', uploadId);
    formData.append('chunkIndex', String(chunkIndex));
    formData.append('totalChunks', String(totalChunks));

    const response = await this.client.post('/files/upload/chunk', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  }

  async completeUpload(
    uploadId: string,
    blockType = 'file'
  ): Promise<UploadCompleteResponse> {
    const response = await this.client.post('/files/upload/complete', {
      uploadId,
      blockType
    });
    return response.data;
  }

  async queryDatabase(
    databaseId: string,
    filter?: any,
    sorts?: any,
    pageSize = 100
  ): Promise<any> {
    const response = await this.client.post('/databases/query', {
      databaseId,
      filter,
      sorts,
      pageSize
    });
    return response.data;
  }
}

export const api = new ApiClient();